import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowRight, PhoneCall, MapPin, Loader2, MessageSquare } from "lucide-react";
import { SBALogo } from "@/components/SBALogo";
import { SbaSteps, AdvisorHelpSection, TpmoDisclosure, ComparePreview } from "@/components/sba/SbaShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const fmtUsPhone = (n) => { const d = (n || "").replace(/\D/g, "").slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : n; };

export default function FounderFrontDoor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API}/f/${encodeURIComponent(slug)}`);
        if (!active) return;
        if (res.status === 404) { setState({ loading: false, data: null, error: "notfound" }); return; }
        if (!res.ok) { setState({ loading: false, data: null, error: "error" }); return; }
        setState({ loading: false, data: await res.json(), error: null });
      } catch {
        if (active) setState({ loading: false, data: null, error: "error" });
      }
    })();
    return () => { active = false; };
  }, [slug]);

  const ref = state.data?.attribution_code || slug;
  const go = (help) => {
    const p = new URLSearchParams(window.location.search);
    p.set("ref", ref);
    if (help) p.set("help", "advisor");
    navigate(`/ask?${p.toString()}`);
  };

  if (state.loading) {
    return <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center" data-testid="frontdoor-loading"><Loader2 className="h-6 w-6 text-[#1E3E62] animate-spin" /></div>;
  }
  if (state.error === "notfound" || !state.data) {
    return (
      <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center px-6" data-testid="frontdoor-notfound">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold text-[#0B192C]">This page isn't available</h1>
          <p className="mt-3 text-[#4B5563]">We couldn't find that advisor page. The link may be inactive.</p>
          <button onClick={() => navigate("/sba")} data-testid="frontdoor-fallback-sba"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-3 font-bold text-[#0B192C]">
            Continue to Senior Benefit Advisors <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  const d = state.data;
  const phone = d.business_phone;
  return (
    <div className="min-h-screen bg-[#FBFBF9] text-[#111827]" data-testid="frontdoor-page">
      <header className="border-b border-[#E5E5E0] bg-[#FBFBF9]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
          <SBALogo className="h-8 sm:h-10" />
          <div className="flex items-center gap-3">
            {phone && (
              <a href={`tel:${phone}`} className="hidden sm:inline-flex items-center gap-2 text-[#1E3E62] font-semibold" data-testid="frontdoor-nav-phone">
                <PhoneCall className="w-4 h-4 text-[#2D6A4F]" /><span className="hidden sm:inline">{fmtUsPhone(phone)}</span>
              </a>
            )}
            <Link to="/login?brand=sba" className="h-11 px-3 sm:px-4 inline-flex items-center whitespace-nowrap rounded-full border border-[#BCCCDC] text-[#0B192C] text-sm sm:text-base" data-testid="frontdoor-signin">Member sign in</Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 pt-10 pb-4 sm:pt-14 grid lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-12 items-start">
        <div>
          <p className="text-sm font-bold uppercase tracking-wide text-[#2D6A4F]">Licensed insurance advisor · Senior Benefit Advisors</p>
          <h1 className="mt-3 font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#0B192C]" data-testid="frontdoor-business">{d.business_display}</h1>
          {d.tagline && <p className="mt-4 text-lg text-[#374151]" data-testid="frontdoor-tagline">{d.tagline}</p>}
          <p className="mt-4 text-lg leading-relaxed text-[#374151] max-w-xl">
            Ask your Medicare questions and explore plan options with Ask Nauticus. When you want human help,
            {" "}{d.business_display} can review your options with you, at no cost for the assistance.
          </p>
          {Array.isArray(d.states) && d.states.length > 0 && (
            <p className="mt-3 flex items-center gap-2 text-base text-[#4B5563]" data-testid="frontdoor-states"><MapPin className="h-4 w-4" /> Licensed in {d.states.join(", ")}</p>
          )}
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button onClick={() => go(false)} className="h-14 px-8 rounded-full bg-[#D4AF37] text-[#0B192C] text-lg font-bold inline-flex items-center justify-center gap-2" data-testid="frontdoor-cta-ask">
              Ask Nauticus <ArrowRight className="h-5 w-5" />
            </button>
            <button onClick={() => go(true)} className="h-14 px-6 rounded-full border-2 border-[#0B192C] text-[#0B192C] text-base font-bold" data-testid="frontdoor-cta-advisor">
              Request help from {d.business_display}
            </button>
          </div>
          {phone && (
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
              <a href={`tel:${phone}`} className="inline-flex items-center gap-2 text-base font-semibold text-[#1E3E62]" data-testid="frontdoor-call">
                <PhoneCall className="w-4 h-4" /> Call {d.business_display} · <span className="select-all">{fmtUsPhone(phone)}</span>
              </a>
              {d.sms_ready && (
                <a href={`sms:${phone}`} className="inline-flex items-center gap-2 text-base font-semibold text-[#1E3E62]" data-testid="frontdoor-text">
                  <MessageSquare className="w-4 h-4" /> Text {(d.business_display || "").split(" ")[0]}
                </a>
              )}
            </div>
          )}
        </div>
        <ComparePreview />
      </section>

      <SbaSteps />
      <AdvisorHelpSection onAdvisor={() => go(true)} who={d.business_display} />

      <footer className="bg-[#0B192C] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 space-y-4">
          <SBALogo light />
          <p className="text-base text-[#DCE4EC]">
            This is a solicitation for insurance. By submitting a request you consent to be contacted about Medicare and
            insurance options. Consent is not a condition of purchase. <Link to="/privacy" className="underline">Privacy</Link>
          </p>
          <TpmoDisclosure dark testid="frontdoor-footer-tpmo" />
        </div>
      </footer>
    </div>
  );
}
